/**
 * Aviso de sessão expirada.
 *
 * Quando o signOut acontece de forma INVOLUNTÁRIA (refresh token
 * rejeitado, usuário banido, sessão revogada em outro device,
 * divergência cross-flow detectada por RoleGuard), gravamos um flag em
 * sessionStorage para que a próxima tela pública (Landing / Auth)
 * mostre um toast ou banner explicando o motivo, em vez do usuário
 * ser silenciosamente jogado pra fora.
 *
 * Voluntário (botão "sair") NÃO grava o flag — é navegação normal.
 */

const KEY = "zappy.session-expired";
const VOLUNTARY_KEY = "zappy.voluntary-signout";

export type SessionExpiredReason =
  | "refresh_failed"
  | "cross_flow"
  | "remote_revoked"
  | "unknown";

const getStorage = () => {
  if (typeof window === "undefined") return null;
  try {
    return window.sessionStorage;
  } catch {
    return null;
  }
};

export const markSessionExpired = (reason: SessionExpiredReason = "unknown") => {
  getStorage()?.setItem(KEY, reason);
};

export const consumeSessionExpired = (): SessionExpiredReason | null => {
  const storage = getStorage();
  if (!storage) return null;
  const value = storage.getItem(KEY);
  if (!value) return null;
  storage.removeItem(KEY);
  return (value as SessionExpiredReason) ?? "unknown";
};

export const markVoluntarySignout = () => {
  getStorage()?.setItem(VOLUNTARY_KEY, "1");
};

export const consumeVoluntarySignout = (): boolean => {
  const storage = getStorage();
  if (!storage) return false;
  const value = storage.getItem(VOLUNTARY_KEY);
  if (!value) return false;
  storage.removeItem(VOLUNTARY_KEY);
  return true;
};

export const SESSION_EXPIRED_STORAGE_KEY = KEY;
export const VOLUNTARY_SIGNOUT_STORAGE_KEY = VOLUNTARY_KEY;

export const SESSION_EXPIRED_MESSAGES: Record<SessionExpiredReason, string> = {
  refresh_failed: "Sua sessão expirou. Faça login novamente para continuar.",
  cross_flow:
    "Detectamos acesso em outro fluxo. Por segurança, encerramos sua sessão.",
  remote_revoked:
    "Sua sessão foi encerrada em outro dispositivo. Faça login novamente.",
  unknown: "Sua sessão foi encerrada. Faça login novamente para continuar.",
};
